// Spreadsheet tool definitions for Claude API

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

// ============================================================================
// Analysis tools (loaded via spreadsheet skill)
// ============================================================================

export const DESCRIBE_SPREADSHEET_TOOL: Tool = {
  name: 'describe_spreadsheet',
  description: 'Get an overview of a spreadsheet: sheet names, column headers, data types, and sample rows. Use this first when analyzing an uploaded spreadsheet.',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet (from list_content or attachment info).',
      },
    },
    required: ['contentId'],
  },
};

export const SUMMARIZE_COLUMN_TOOL: Tool = {
  name: 'summarize_column',
  description: 'Get statistics for a spreadsheet column: count, min, max, mean, median, sum, and unique values. Specify column by number (1-based), letter ("A"-"ZZ"), or header name.',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet.',
      },
      column: {
        type: ['string', 'number'],
        description: 'Column identifier: 1-based number, letter (e.g. "B"), or header name (e.g. "Revenue").',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet name (defaults to first sheet).',
      },
      headerRow: {
        type: 'number',
        description: 'Row number containing headers (default: 1).',
      },
    },
    required: ['contentId', 'column'],
  },
};

export const QUERY_CELLS_TOOL: Tool = {
  name: 'query_cells',
  description: 'Read specific cells from a spreadsheet. Supports ranges like "A1:D10", "B:B", "3:3", "A1", "A:D", "2:5", and comma-separated cells like "E2,E38,E44". Max 1000 cells.',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet.',
      },
      range: {
        type: 'string',
        description: 'Cell range (e.g. "A1:D10", "B:B", "3:3", "A1,B5,C10").',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet name (defaults to first sheet).',
      },
    },
    required: ['contentId', 'range'],
  },
};

export const EXTRACT_SPREADSHEET_TEXT_TOOL: Tool = {
  name: 'extract_spreadsheet_text',
  description: 'Extract the full text content of a spreadsheet as tab-separated values (TSV). Returns all cell values row-by-row. Use this when you need to read the actual data. Max 200 rows per call; use startRow for pagination.',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet (from list_content or attachment info).',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet name to extract (defaults to first sheet).',
      },
      startRow: {
        type: 'number',
        description: '1-based starting row (default: 1). Use for pagination when previous call was truncated.',
      },
    },
    required: ['contentId'],
  },
};

export const RUN_FORMULA_TOOL: Tool = {
  name: 'run_formula',
  description: 'Evaluate a single Excel formula against an existing spreadsheet and return the computed result. Use this to verify formulas or compute ad-hoc values without modifying the spreadsheet.',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet.',
      },
      formula: {
        type: 'string',
        description: 'Excel formula to evaluate (e.g. "=SUM(A1:A10)").',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet context (defaults to first sheet).',
      },
    },
    required: ['contentId', 'formula'],
  },
};

export const FILTER_SPREADSHEET_TOOL: Tool = {
  name: 'filter_spreadsheet',
  description: 'Filter spreadsheet rows by multi-column conditions. Returns matching rows with their data. Useful for finding rows where specific columns match certain criteria (e.g. column C = "无" AND column D = "无").',
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the spreadsheet.',
      },
      conditions: {
        type: 'array',
        description: 'Array of filter conditions to apply.',
        items: {
          type: 'object',
          properties: {
            column: {
              type: ['string', 'number'],
              description: 'Column identifier: letter (e.g. "C"), 1-based number, or header name.',
            },
            operator: {
              type: 'string',
              enum: ['equals', 'notEquals', 'contains', 'notContains', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'between', 'isEmpty', 'isNotEmpty'],
              description: 'Comparison operator.',
            },
            value: {
              description: 'Value to compare against (not needed for isEmpty/isNotEmpty).',
            },
            value2: {
              description: 'Second value for "between" operator (upper bound).',
            },
          },
          required: ['column', 'operator'],
        },
      },
      logic: {
        type: 'string',
        enum: ['AND', 'OR'],
        description: 'How to combine conditions (default: AND).',
      },
      returnColumns: {
        type: 'array',
        description: 'Columns to include in results (default: all). Array of column letters, numbers, or header names.',
        items: {
          type: ['string', 'number'],
        },
      },
      maxRows: {
        type: 'number',
        description: 'Maximum rows to return (default: 200, max: 200).',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet name (defaults to first sheet).',
      },
      headerRow: {
        type: 'number',
        description: 'Row number containing headers (default: 1).',
      },
    },
    required: ['contentId', 'conditions'],
  },
};

// ============================================================================
// Editing tool (cell-level patching)
// ============================================================================

export const EDIT_SPREADSHEET_TOOL: Tool = {
  name: 'edit_spreadsheet',
  description: `Edit specific cells in an existing spreadsheet without regenerating the entire thing. Use this for targeted changes: updating values, adding formulas, changing formatting, inserting/deleting rows or columns.

Each edit targets a cell by reference (e.g. "A1", "B5", "AA10") and merges properties onto the existing cell. Only specified properties are changed; unspecified properties are preserved.

Structural operations (insertRows, deleteRows, insertCols, deleteCols) are applied before cell edits.

Use edit_spreadsheet instead of generate_spreadsheet when:
- Changing a few cell values or formulas
- Adding/updating formatting on specific cells
- Inserting or deleting rows/columns
- Any targeted modification to an existing spreadsheet

Use generate_spreadsheet when:
- Creating a new spreadsheet from scratch
- Rewriting most of the spreadsheet content`,
  input_schema: {
    type: 'object' as const,
    properties: {
      contentId: {
        type: 'string',
        description: 'The content ID of the existing spreadsheet to edit.',
      },
      sheetName: {
        type: 'string',
        description: 'Sheet to edit (defaults to first sheet).',
      },
      edits: {
        type: 'array',
        description: 'Array of cell edits to apply.',
        items: {
          type: 'object',
          properties: {
            cell: { type: 'string', description: 'Cell reference (e.g. "A1", "B5", "AA10").' },
            value: { description: 'Cell value (string, number, or boolean).' },
            formula: { type: 'string', description: 'Excel formula (e.g. "=SUM(A1:A10)").' },
            bold: { type: 'boolean' },
            italic: { type: 'boolean' },
            fontColor: { type: 'string', description: 'Hex color without # (e.g. "0000FF").' },
            fontSize: { type: 'number' },
            fontName: { type: 'string' },
            fillColor: { type: 'string', description: 'Background hex color without #.' },
            numberFormat: { type: 'string', description: 'Excel number format (e.g. "$#,##0").' },
            alignment: { type: 'string', enum: ['left', 'center', 'right'] },
            verticalAlignment: { type: 'string', enum: ['top', 'middle', 'bottom'] },
            wrapText: { type: 'boolean' },
          },
          required: ['cell'],
        },
      },
      insertRows: {
        type: 'array',
        description: 'Insert empty rows. Each entry: { after: rowNumber (1-based), count: numberOfRows }.',
        items: {
          type: 'object',
          properties: {
            after: { type: 'number', description: '1-based row number after which to insert. Use 0 to insert at the top.' },
            count: { type: 'number', description: 'Number of rows to insert (default: 1).' },
          },
          required: ['after'],
        },
      },
      deleteRows: {
        type: 'array',
        description: '1-based row numbers to delete.',
        items: { type: 'number' },
      },
      insertCols: {
        type: 'array',
        description: 'Insert columns. Each entry: { after: "C" (column letter), count: numberOfCols }.',
        items: {
          type: 'object',
          properties: {
            after: { type: 'string', description: 'Column letter after which to insert. Use "" to insert at the left.' },
            count: { type: 'number', description: 'Number of columns to insert (default: 1).' },
          },
          required: ['after'],
        },
      },
      deleteCols: {
        type: 'array',
        description: 'Column letters to delete (e.g. ["D", "F"]).',
        items: { type: 'string' },
      },
    },
    required: ['contentId', 'edits'],
  },
};

// ============================================================================
// Generation tool
// ============================================================================

const spreadsheetExampleJson = `{
  "type": "spreadsheet",
  "title": "Q1 Revenue Summary",
  "sheets": [
    {
      "name": "Revenue",
      "columns": [
        { "width": 15 },
        { "width": 12 },
        { "width": 12 },
        { "width": 12 },
        { "width": 14 }
      ],
      "rows": [
        {
          "cells": [
            { "value": "Product", "bold": true, "fillColor": "D5E8F0" },
            { "value": "Jan", "bold": true, "fillColor": "D5E8F0", "alignment": "center" },
            { "value": "Feb", "bold": true, "fillColor": "D5E8F0", "alignment": "center" },
            { "value": "Mar", "bold": true, "fillColor": "D5E8F0", "alignment": "center" },
            { "value": "Total", "bold": true, "fillColor": "D5E8F0", "alignment": "center" }
          ]
        },
        {
          "cells": [
            { "value": "Widget A" },
            { "value": 15000, "numberFormat": "$#,##0" },
            { "value": 18000, "numberFormat": "$#,##0" },
            { "value": 21000, "numberFormat": "$#,##0" },
            { "formula": "=SUM(B2:D2)", "numberFormat": "$#,##0", "bold": true }
          ]
        },
        {
          "cells": [
            { "value": "Widget B" },
            { "value": 9500, "numberFormat": "$#,##0" },
            { "value": 11200, "numberFormat": "$#,##0" },
            { "value": 13800, "numberFormat": "$#,##0" },
            { "formula": "=SUM(B3:D3)", "numberFormat": "$#,##0", "bold": true }
          ]
        },
        {
          "cells": [
            { "value": "Grand Total", "bold": true },
            { "formula": "=SUM(B2:B3)", "numberFormat": "$#,##0", "bold": true },
            { "formula": "=SUM(C2:C3)", "numberFormat": "$#,##0", "bold": true },
            { "formula": "=SUM(D2:D3)", "numberFormat": "$#,##0", "bold": true },
            { "formula": "=SUM(E2:E3)", "numberFormat": "$#,##0", "bold": true }
          ]
        }
      ],
      "freezePane": { "row": 1 }
    }
  ]
}`;

export const SPREADSHEET_GENERATION_TOOL: Tool = {
  name: 'generate_spreadsheet',
  description: `Generate an Excel spreadsheet (.xlsx) with data, formulas, and formatting. Use this tool when the user asks for a spreadsheet, Excel file, financial model, data table, or similar tabular output.

Cell properties:
- value: string | number | boolean — the cell value
- formula: string — Excel formula (e.g. "=SUM(A1:A10)"). Use formulas, not hardcoded calculations.
- bold, italic: boolean — font style
- fontColor, fillColor: string — hex color without # (e.g. "0000FF" for blue)
- fontSize: number — font size in points
- fontName: string — font family (e.g. "Arial")
- numberFormat: string — Excel format code (e.g. "$#,##0.00", "0.0%")
- alignment: "left" | "center" | "right"
- verticalAlignment: "top" | "middle" | "bottom"
- wrapText: boolean
- border: { top, right, bottom, left } each with { style, color }

Sheet features:
- columns: array of { width } for column widths
- freezePane: { row, col } to freeze rows/columns
- mergedCells: array of { startRow, startCol, endRow, endCol } (1-based)

Financial model conventions:
- Blue text (0000FF): Hardcoded inputs
- Black text (000000): Formulas
- Green text (008000): Cross-sheet references
- Use formulas (=SUM, =AVERAGE, etc.) instead of calculating values

Example:
${spreadsheetExampleJson}

When editing an existing spreadsheet, include the spreadsheetId from get_content. For new spreadsheets, omit spreadsheetId.`,
  input_schema: {
    type: 'object' as const,
    properties: {
      spreadsheetId: {
        type: 'string',
        description: 'If editing an existing spreadsheet, set this to the content ID returned by get_content. Omit for new spreadsheets.',
      },
      type: {
        type: 'string',
        enum: ['spreadsheet'],
        description: 'Must be "spreadsheet"',
      },
      title: {
        type: 'string',
        description: 'The spreadsheet title',
      },
      sheets: {
        type: 'array',
        description: 'Array of sheets in the workbook',
        items: {
          type: 'object',
          properties: {
            name: { type: 'string', description: 'Sheet tab name' },
            columns: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  width: { type: 'number', description: 'Column width in characters' },
                },
              },
            },
            rows: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  cells: {
                    type: 'array',
                    items: {
                      type: 'object',
                      properties: {
                        value: { description: 'Cell value (string, number, or boolean)' },
                        formula: { type: 'string', description: 'Excel formula (e.g. "=SUM(A1:A10)")' },
                        bold: { type: 'boolean' },
                        italic: { type: 'boolean' },
                        fontColor: { type: 'string', description: 'Hex color without # (e.g. "0000FF")' },
                        fontSize: { type: 'number' },
                        fontName: { type: 'string' },
                        fillColor: { type: 'string', description: 'Background hex color without #' },
                        numberFormat: { type: 'string', description: 'Excel number format (e.g. "$#,##0")' },
                        alignment: { type: 'string', enum: ['left', 'center', 'right'] },
                        verticalAlignment: { type: 'string', enum: ['top', 'middle', 'bottom'] },
                        wrapText: { type: 'boolean' },
                      },
                    },
                  },
                  height: { type: 'number', description: 'Row height in points' },
                },
                required: ['cells'],
              },
            },
            freezePane: {
              type: 'object',
              properties: {
                row: { type: 'number', description: 'Number of rows to freeze from top' },
                col: { type: 'number', description: 'Number of columns to freeze from left' },
              },
            },
            mergedCells: {
              type: 'array',
              items: {
                type: 'object',
                properties: {
                  startRow: { type: 'number' },
                  startCol: { type: 'number' },
                  endRow: { type: 'number' },
                  endCol: { type: 'number' },
                },
                required: ['startRow', 'startCol', 'endRow', 'endCol'],
              },
            },
          },
          required: ['name', 'rows'],
        },
      },
    },
    required: ['type', 'sheets'],
  },
};
